import React from "react";
import {
	FaArrowRight,
	FaCalendarCheck,
	FaComments,
	FaHeart,
	FaMapMarkerAlt,
	FaPaw,
	FaShieldAlt,
	FaSlidersH,
	FaStar,
} from "react-icons/fa";
import {appStoreLink} from "../../js/helpers/functions";
import styles from "./styles.module.css";
import discoverScreen from "./app-discover.jpeg";
import profileDetailScreen from "./app-profile-detail.jpeg";
import filtersScreen from "./app-filters.jpeg";
import matchScreen from "./app-match.jpeg";
import parksScreen from "./app-pawdate-map.jpeg";
import chatScreen from "./app-pawdate-chat.jpeg";
import profileScreen from "./app-profile.jpeg";
import appStoreBadge from "./appstore.png";
import playStoreBadge from "./playstore.png";

const Phone = ({src, alt, className = ""}) => (
	<div className={`${styles.phone} ${className}`}>
		<img src={src} alt={alt} loading="lazy"/>
	</div>
);

const StoreButtons = ({compact = false}) => (
	<div className={`${styles.storeButtons} ${compact ? styles.storeButtonsCompact : ""}`}>
		<a
			href="https://apps.apple.com/us/app/barkr-meet-dog-owners/id6444854210"
			aria-label="Download Barkr on the App Store">
			<img src={appStoreBadge} alt="Download on the App Store"/>
		</a>
		<a
			href="https://play.google.com/store/apps/details?id=com.barkr.barkr"
			aria-label="Get Barkr on Google Play">
			<img src={playStoreBadge} alt="Get it on Google Play"/>
		</a>
	</div>
);

const FeatureCard = ({icon, eyebrow, title, children, tone}) => (
	<article className={styles.featureCard}>
		<div className={`${styles.featureIcon} ${styles[tone]}`}>{icon}</div>
		<p className={styles.cardEyebrow}>{eyebrow}</p>
		<h3>{title}</h3>
		<p>{children}</p>
	</article>
);

function Home() {
	return (
		<main className={styles.page}>
			<section className={styles.hero}>
				<div className={styles.heroGlowOne}/>
				<div className={styles.heroGlowTwo}/>
				<div className={styles.shell}>
					<div className={styles.heroGrid}>
						<div className={styles.heroCopy}>
							<div className={styles.eyebrowPill}>
								<FaPaw/>
								<span>Made for dogs and their people</span>
							</div>
							<h1>Better walks start with <span>better company.</span></h1>
							<p className={styles.heroLead}>
								Meet nearby dog parents, find the right playmates, and turn an ordinary walk
								into your dog’s favorite part of the day.
							</p>
							<div className={styles.heroActions}>
								<button type="button" onClick={appStoreLink} className={styles.primaryButton}>
									Get Barkr
									<FaArrowRight/>
								</button>
								<a href="#how-it-works" className={styles.secondaryButton}>See how it works</a>
							</div>
							<div className={styles.trustRow}>
								<div className={styles.avatarStack}>
									<span role="img" aria-label="dog">🐕</span>
									<span role="img" aria-label="puppy">🐶</span>
									<span role="img" aria-label="guide dog">🦮</span>
								</div>
								<div>
									<div className={styles.stars}>
										<FaStar/><FaStar/><FaStar/><FaStar/><FaStar/>
									</div>
									<p>New friendships, one sniff at a time</p>
								</div>
							</div>
						</div>

						<div className={styles.heroVisual} aria-label="Barkr app preview">
							<div className={styles.visualOrbit}/>
							<div className={`${styles.floatingCard} ${styles.nearbyCard}`}>
								<div className={styles.floatingIcon}><FaMapMarkerAlt/></div>
								<div><strong>12 nearby</strong><span>friendly playmates</span></div>
							</div>
							<Phone src={discoverScreen} alt="Discover dogs nearby in Barkr"/>
							<div className={`${styles.floatingCard} ${styles.matchCard}`}>
								<div className={`${styles.floatingIcon} ${styles.coralIcon}`}><FaHeart/></div>
								<div><strong>It’s a match!</strong><span>Time to say hello</span></div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className={styles.proofBar} aria-label="Barkr benefits">
				<div className={styles.shell}>
					<div className={styles.proofGrid}>
						<div><FaMapMarkerAlt/><span><strong>Local first</strong>Meet dogs around you</span></div>
						<div><FaShieldAlt/><span><strong>Private by design</strong>Exact locations stay private</span></div>
						<div><FaCalendarCheck/><span><strong>Easy pawdates</strong>Plan together in chat</span></div>
					</div>
				</div>
			</section>

			<section id="how-it-works" className={styles.featuresSection}>
				<div className={styles.shell}>
					<div className={styles.sectionHeading}>
						<p className={styles.sectionEyebrow}>YOUR DOG’S SOCIAL CIRCLE</p>
						<h2>Everything you need to find their kind of pack.</h2>
						<p>Less scrolling, more tail wagging. Barkr helps the right dogs—and people—find each other.</p>
					</div>
					<div className={styles.featureGrid}>
						<FeatureCard
							icon={<FaPaw/>}
							eyebrow="DISCOVER"
							title="Profiles with personality"
							tone="indigo">
							See the dog, their person, temperament, interests, and the details that make a good match.
						</FeatureCard>
						<FeatureCard
							icon={<FaSlidersH/>}
							eyebrow="PREFERENCES"
							title="Find the right fit"
							tone="coral">
							Filter by distance, age, breed, and more without losing the joy of discovery.
						</FeatureCard>
						<FeatureCard
							icon={<FaComments/>}
							eyebrow="CONNECT"
							title="From hello to pawdate"
							tone="mint">
							Chat safely, choose a dog-friendly place, and share the plan without leaving Barkr.
						</FeatureCard>
					</div>

					<div className={styles.interfaceGrid}>
						<article className={`${styles.interfaceCard} ${styles.interfaceCardFilters}`}>
							<div className={styles.interfaceCopy}>
								<p className={styles.cardEyebrow}>MAKE IT YOUR PACK</p>
								<h3>Preferences that stay simple.</h3>
								<p>
									Choose who you would like to meet, set comfortable age ranges, and
									narrow by breed without turning discovery into a spreadsheet.
								</p>
							</div>
							<div className={styles.interfaceScreen}>
								<img src={filtersScreen} alt="Barkr discovery preference filters" loading="lazy"/>
							</div>
						</article>
						<article className={`${styles.interfaceCard} ${styles.interfaceCardProfile}`}>
							<div className={styles.interfaceCopy}>
								<p className={styles.cardEyebrow}>EVERYTHING CLOSE AT PAW</p>
								<h3>A profile that feels organized.</h3>
								<p>
									Switch between dog and person profiles, find nearby parks, manage
									your account, and reach support from one clear home base.
								</p>
							</div>
							<div className={styles.interfaceScreen}>
								<img src={profileScreen} alt="Barkr profile and shortcuts screen" loading="lazy"/>
							</div>
						</article>
					</div>
				</div>
			</section>

			<section className={styles.showcaseSection}>
				<div className={styles.shell}>
					<div className={styles.showcaseGrid}>
						<div className={styles.showcaseVisual}>
							<div className={styles.phoneBackdrop}/>
							<Phone src={profileDetailScreen} alt="A detailed dog profile in Barkr"/>
							<div className={styles.miniPreference}>
								<FaSlidersH/>
								<div><strong>Your preferences</strong><span>Distance · breed · age</span></div>
							</div>
						</div>
						<div className={styles.showcaseCopy}>
							<p className={styles.sectionEyebrow}>DISCOVER NATURALLY</p>
							<h2>A first impression that feels like more than a photo.</h2>
							<p>
								Thoughtful profiles put dogs at the center while giving you enough context to
								choose confidently. Swipe, browse photos, and open the full story whenever you want.
							</p>
							<ul>
								<li><span><FaHeart/></span>Dog and person profiles in one clear view</li>
								<li><span><FaPaw/></span>Temperament, routines, interests, and essentials</li>
								<li><span><FaShieldAlt/></span>Approximate distance without exposing exact location</li>
							</ul>
						</div>
					</div>
				</div>
			</section>

			<section className={`${styles.showcaseSection} ${styles.matchShowcase}`}>
				<div className={styles.shell}>
					<div className={`${styles.showcaseGrid} ${styles.reverseGrid}`}>
						<div className={styles.showcaseCopy}>
							<p className={styles.sectionEyebrow}>A MUTUAL TAIL WAG</p>
							<h2>Match, chat, then make the walk happen.</h2>
							<p>
								When the interest is mutual, Barkr makes the next step easy. Start a conversation,
								share reactions, and plan somewhere both dogs can feel comfortable.
							</p>
							<div className={styles.quoteCard}>
								<div className={styles.quoteMark}>“</div>
								<p>We found a calm walking buddy nearby—and now Sunday mornings are the highlight of both dogs’ week.</p>
								<span>Happy Barkr pack</span>
							</div>
						</div>
						<div className={styles.matchVisual}>
							<Phone src={matchScreen} alt="A new match in Barkr"/>
							<div className={styles.matchHalo}/>
							<div className={styles.heartBadge}><FaHeart/></div>
						</div>
					</div>
				</div>
			</section>

			<section className={styles.pawdateSection}>
				<div className={styles.shell}>
					<div className={styles.sectionHeading}>
						<p className={styles.sectionEyebrow}>PLAN THE GOOD PART</p>
						<h2>Pick a place. Send the invite. Grab the leash.</h2>
						<p>Dog-friendly places and the conversation live together, so plans never get lost.</p>
					</div>
					<div className={styles.pawdateStage}>
						<Phone src={parksScreen} alt="Choose a nearby park with Barkr" className={styles.leftPhone}/>
						<div className={styles.routeLine}>
							<span/><span/><span/>
						</div>
						<div className={styles.pawdateCenter}>
							<div className={styles.mapPin}><FaMapMarkerAlt/></div>
							<p>PAWDATE CONFIRMED</p>
							<h3>Saturday · 10:30</h3>
							<span>South Park · easy walking trail</span>
						</div>
						<Phone src={chatScreen} alt="Plan a pawdate in Barkr chat" className={styles.rightPhone}/>
					</div>
				</div>
			</section>

			<section id="safety" className={styles.safetySection}>
				<div className={styles.shell}>
					<div className={styles.safetyCard}>
						<div className={styles.safetyIcon}><FaShieldAlt/></div>
						<div>
							<p className={styles.sectionEyebrow}>SAFETY, WITHOUT THE LECTURE</p>
							<h2>Share the vibe, not your exact location.</h2>
							<p>
								Barkr shows approximate distance, gives you reporting controls, and keeps meetup
								planning in one place. You decide what to share and when.
							</p>
						</div>
						<a href="/child-safety">Read our safety approach <FaArrowRight/></a>
					</div>
				</div>
			</section>

			<section className={styles.ctaSection}>
				<div className={styles.ctaGlow}/>
				<div className={styles.shell}>
					<div className={styles.ctaCard}>
						<div>
							<p className={styles.sectionEyebrow}>THE PACK IS WAITING</p>
							<h2>Make the next walk the start of something good.</h2>
							<p>Download Barkr and help your dog find their next favorite friend.</p>
							<StoreButtons/>
						</div>
						<div className={styles.ctaVisual}>
							<div className={styles.ctaPaw}><FaPaw/></div>
							<div className={styles.ctaBubbleOne}>New match <FaHeart/></div>
							<div className={styles.ctaBubbleTwo}>Walk at 10? <FaComments/></div>
						</div>
					</div>
				</div>
			</section>
		</main>
	);
}

export default Home;
